import type { BusinessBriefData } from '@doneverse/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { businessBriefOptions } from '../queries/index.js'
import { profileService } from '../services/index.js'
import { BUSINESS_BRIEF_SCHEMA } from '../utils/business-brief-schema.js'
import { buildBriefFormSchema, toBriefValues } from '../utils/brief-validation.js'

const businessBriefSchema = buildBriefFormSchema(BUSINESS_BRIEF_SCHEMA)

export interface BusinessBriefStatus {
  hasBrief: boolean
  isValid: boolean
  issueCount: number
}

export function getBusinessBriefStatus(brief: BusinessBriefData | null | undefined): BusinessBriefStatus {
  const values = toBriefValues(brief)
  const result = businessBriefSchema.safeParse(values)

  return {
    hasBrief: Object.keys(values).length > 0,
    isValid: result.success,
    issueCount: result.success ? 0 : result.error.issues.length,
  }
}

export function useMyBrief() {
  return useQuery(businessBriefOptions())
}

export function useBusinessBriefStatus({ enabled = true }: { enabled?: boolean } = {}) {
  const query = useQuery({
    ...businessBriefOptions(),
    enabled,
  })

  const brief = query.data?.data ?? null
  const status = useMemo(() => getBusinessBriefStatus(brief), [brief])

  return {
    data: brief,
    // Bumped by the DB trigger on every write, so it tracks the last edit —
    // founder resubmit or Typeform webhook alike.
    updatedAt: query.data?.updated_at ?? null,
    isLoading: query.isLoading,
    status: query.status,
    fetchStatus: query.fetchStatus,
    error: query.error,
    hasBrief: status.hasBrief,
    isValid: status.isValid,
    issueCount: status.issueCount,
    needsAttention: enabled && query.status === 'success' && !status.isValid,
  }
}

export function useUpdateBusinessBrief() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (brief: BusinessBriefData) => {
      await profileService.updateBusinessBrief(brief)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessBriefOptions().queryKey })
    },
  })
}
